import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import AmenitySelector from "@/components/property/AmenitySelector";
import { ROOM_STATUS_OPTIONS } from "@/components/property/roomMeta";

const inputCls =
  "w-full rounded-xl border border-border bg-secondary/40 px-3.5 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary focus:bg-background transition-colors";

function Field({ label, required, error, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="text-sm font-medium text-foreground">
        {label} {required && <span className="text-destructive">*</span>}
      </label>
      <div className="mt-1">{children}</div>
      {error && <p className="mt-1 text-xs text-destructive" data-testid={`${testid}-error`}>{error}</p>}
    </div>
  );
}

const EMPTY = {
  room_number: "", name: "", description: "",
  price_daily: "", price_monthly: "", price_yearly: "", deposit_amount: "", dp_rate: "",
  size: "", floor: "", status: "available",
};

export default function RoomForm({ open, onOpenChange, propertyId, room, onSaved }) {
  const isEdit = !!room;
  const [form, setForm] = useState(EMPTY);
  const [amenityIds, setAmenityIds] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setFormError("");
    if (room) {
      setForm({
        room_number: room.room_number || "",
        name: room.name || "",
        description: room.description || "",
        price_daily: room.price_daily ?? "",
        price_monthly: room.price_monthly ?? "",
        price_yearly: room.price_yearly ?? "",
        deposit_amount: room.deposit_amount ?? "",
        dp_rate: room.dp_rate ?? "",
        size: room.size ?? "",
        floor: room.floor ?? "",
        status: room.status || "available",
      });
      setAmenityIds((room.amenities || []).map((a) => a.id));
    } else {
      setForm(EMPTY);
      setAmenityIds([]);
    }
  }, [open, room]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const numOrNull = (v) => (v === "" || v === null ? null : Number(v));

  const validate = () => {
    const e = {};
    if (!form.room_number.trim()) e.room_number = "Nomor kamar wajib diisi";
    for (const [k, label] of [
      ["price_daily", "Harga harian"], ["price_monthly", "Harga bulanan"],
      ["price_yearly", "Harga tahunan"], ["deposit_amount", "Deposit"], ["size", "Ukuran"],
    ]) {
      if (form[k] !== "" && Number(form[k]) < 0) e[k] = `${label} tidak boleh negatif`;
    }
    if (form.dp_rate !== "") {
      const v = Number(form.dp_rate);
      if (v < 0 || v > 100) e.dp_rate = "DP Rate harus antara 0–100";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setFormError("");
    if (!validate()) return;
    setLoading(true);
    const payload = {
      room_number: form.room_number.trim(),
      name: form.name.trim() || null,
      description: form.description.trim() || null,
      price_daily: numOrNull(form.price_daily),
      price_monthly: numOrNull(form.price_monthly),
      price_yearly: numOrNull(form.price_yearly),
      deposit_amount: numOrNull(form.deposit_amount),
      dp_rate: form.dp_rate === "" ? null : parseInt(form.dp_rate, 10),
      size: numOrNull(form.size),
      floor: form.floor === "" ? null : parseInt(form.floor, 10),
      status: form.status,
    };
    try {
      let saved;
      if (isEdit) {
        ({ data: saved } = await apiClient.put(`/rooms/${room.id}`, payload));
      } else {
        ({ data: saved } = await apiClient.post(`/properties/${propertyId}/rooms`, payload));
      }
      await apiClient.put(`/rooms/${saved.id}/amenities`, { amenity_ids: amenityIds });
      toast.success(isEdit ? "Kamar diperbarui" : "Kamar ditambahkan");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      setFormError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="room-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Kamar" : "Tambah Kamar"}</DialogTitle>
          <DialogDescription>Lengkapi detail kamar. Nomor kamar harus unik dalam property.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="room-form">
          {formError && (
            <div className="rounded-xl bg-destructive/10 border border-destructive/30 px-4 py-2.5 text-sm text-destructive" data-testid="room-form-error">
              {formError}
            </div>
          )}

          {/* Informasi */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nomor Kamar" required error={errors.room_number} testid="room-field-number">
              <input className={inputCls} data-testid="room-number-input" value={form.room_number}
                onChange={(e) => set("room_number", e.target.value)} placeholder="Contoh: A01" />
            </Field>
            <Field label="Nama Kamar" testid="room-field-name">
              <input className={inputCls} data-testid="room-name-input" value={form.name}
                onChange={(e) => set("name", e.target.value)} placeholder="Contoh: Kamar Standar" />
            </Field>
          </div>
          <Field label="Deskripsi" testid="room-field-description">
            <textarea rows={2} className={inputCls} data-testid="room-description-input" value={form.description}
              onChange={(e) => set("description", e.target.value)} placeholder="Opsional" />
          </Field>

          {/* Harga */}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">Harga</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Harga Bulanan (Rp)" error={errors.price_monthly} testid="room-field-monthly">
                <input type="number" step="any" min="0" className={inputCls} data-testid="room-price-monthly-input"
                  value={form.price_monthly} onChange={(e) => set("price_monthly", e.target.value)} placeholder="1000000" />
              </Field>
              <Field label="Harga Harian (Rp)" error={errors.price_daily} testid="room-field-daily">
                <input type="number" step="any" min="0" className={inputCls} data-testid="room-price-daily-input"
                  value={form.price_daily} onChange={(e) => set("price_daily", e.target.value)} placeholder="Opsional" />
              </Field>
              <Field label="Harga Tahunan (Rp)" error={errors.price_yearly} testid="room-field-yearly">
                <input type="number" step="any" min="0" className={inputCls} data-testid="room-price-yearly-input"
                  value={form.price_yearly} onChange={(e) => set("price_yearly", e.target.value)} placeholder="Opsional" />
              </Field>
              <Field label="Deposit (Rp)" error={errors.deposit_amount} testid="room-field-deposit">
                <input type="number" step="any" min="0" className={inputCls} data-testid="room-deposit-input"
                  value={form.deposit_amount} onChange={(e) => set("deposit_amount", e.target.value)} placeholder="Opsional" />
              </Field>
              <Field label="DP Rate (%)" error={errors.dp_rate} testid="room-field-dp-rate">
                <input type="number" step="1" min="0" max="100" className={inputCls} data-testid="room-dp-rate-input"
                  value={form.dp_rate} onChange={(e) => set("dp_rate", e.target.value)} placeholder="mis. 30 (min 20, 100 = lunas)" />
                <p className="mt-1 text-xs text-muted-foreground">Min 20%. Di bawah 20% tidak dapat dibayar. 100% = bayar penuh. Kosong = 100%.</p>
              </Field>
            </div>
          </div>

          {/* Detail */}
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Ukuran (m²)" error={errors.size} testid="room-field-size">
              <input type="number" step="any" min="0" className={inputCls} data-testid="room-size-input"
                value={form.size} onChange={(e) => set("size", e.target.value)} placeholder="12" />
            </Field>
            <Field label="Lantai" testid="room-field-floor">
              <input type="number" step="1" className={inputCls} data-testid="room-floor-input"
                value={form.floor} onChange={(e) => set("floor", e.target.value)} placeholder="1" />
            </Field>
            <Field label="Status" testid="room-field-status">
              <select className={inputCls} data-testid="room-status-select" value={form.status}
                onChange={(e) => set("status", e.target.value)}>
                {ROOM_STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
          </div>

          {/* Amenities (room only) */}
          <div>
            <AmenitySelector selected={amenityIds} onChange={setAmenityIds} category="room" />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" disabled={loading} data-testid="room-submit-button"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60">
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? "Menyimpan..." : isEdit ? "Simpan Perubahan" : "Tambah Kamar"}
            </button>
            <button type="button" onClick={() => onOpenChange(false)} data-testid="room-cancel-button"
              className="px-5 py-2.5 rounded-full border border-border text-primary font-semibold hover:bg-secondary/60 transition-colors">
              Batal
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
